import { useState, useRef, useCallback, useEffect } from "react";
import type { MouseEvent, TouchEvent, ChangeEvent } from "react";

interface TimePickerProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES_FIVE = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const MINUTES_ALL = Array.from({ length: 60 }, (_, i) => i);

const ITEM_HEIGHT = 32;
const CONTAINER_HEIGHT = 112; // h-28

interface ScrollPickerProps {
  readonly items: readonly number[];
  readonly value: number;
  readonly onChange: (value: number) => void;
}

function ScrollPicker({ items, value, onChange }: ScrollPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [centerIndex, setCenterIndex] = useState(() => items.indexOf(value));
  const startY = useRef(0);
  const startScrollTop = useRef(0);
  const momentumRef = useRef<{ vx: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const centerIndexRef = useRef(centerIndex);
  const onChangeRef = useRef(onChange);
  const itemsRef = useRef(items);
  const isSyncingRef = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const currentIndex = items.indexOf(value);
  const targetScrollTop = currentIndex * ITEM_HEIGHT;

  // Scroll event listener for real-time tracking + value emit
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (isSyncingRef.current) return;
      const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(itemsRef.current.length - 1, idx));
      if (centerIndexRef.current !== clamped) {
        centerIndexRef.current = clamped;
        setCenterIndex(clamped);
        onChangeRef.current(itemsRef.current[clamped]);
      }
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!containerRef.current) return;

    const delta = e.deltaY;
    containerRef.current.scrollBy({
      top: delta,
      behavior: "auto",
    });

    // Cancel any ongoing momentum
    momentumRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Snap after scroll ends
    const snap = () => {
      if (!containerRef.current) return;
      const currentScroll = containerRef.current.scrollTop;
      const nearestIndex = Math.round(currentScroll / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(items.length - 1, nearestIndex));
      const targetScroll = clampedIndex * ITEM_HEIGHT;

      containerRef.current.scrollTo({
        top: targetScroll,
        behavior: "instant",
      });
      centerIndexRef.current = clampedIndex;
      onChange(items[clampedIndex]);
    };
    const timeout = setTimeout(snap, 150);
    return () => clearTimeout(timeout);
  }, [items, onChange]);

  // Mouse drag handlers
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    startY.current = e.clientY;
    startScrollTop.current = containerRef.current?.scrollTop || 0;
    momentumRef.current = null;
  };

  // Touch drag handlers
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    startY.current = e.touches[0].clientY;
    startScrollTop.current = containerRef.current?.scrollTop || 0;
    momentumRef.current = { vx: 0 };
  };

  const handleMove = (clientY: number) => {
    if (!containerRef.current) return;
    const deltaY = clientY - startY.current;
    containerRef.current.scrollTop = startScrollTop.current - deltaY;

    // Track velocity for momentum
    if (momentumRef.current) {
      const vx = -deltaY * 0.5;
      momentumRef.current = {
        vx: momentumRef.current.vx * 0.7 + vx * 0.3,
      };
    }
  };

  const handleMouseMove = useCallback((e: Event) => {
    if (!isDragging) return;
    handleMove((e as unknown as MouseEvent).clientY);
  }, [isDragging]);

  const handleTouchMove = useCallback((e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientY);
  }, [isDragging]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);

    if (!containerRef.current) return;

    // Start momentum or snap
    const snap = () => {
      const currentScroll = containerRef.current?.scrollTop || 0;
      const nearestIndex = Math.round(currentScroll / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(items.length - 1, nearestIndex));
      const targetScroll = clampedIndex * ITEM_HEIGHT;

      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: targetScroll,
          behavior: "smooth",
        });
      }
      centerIndexRef.current = clampedIndex;
      onChange(items[clampedIndex]);
    };

    if (momentumRef.current && Math.abs(momentumRef.current.vx) > 0.5) {
      const animate = () => {
        if (!containerRef.current || !momentumRef.current) return;
        const { vx } = momentumRef.current;
        if (Math.abs(vx) < 0.5) {
          momentumRef.current = null;
          snap();
          return;
        }

        containerRef.current.scrollTop -= vx;
            momentumRef.current = { vx: vx * 0.95 };
        rafRef.current = requestAnimationFrame(animate);
      };
      animate();
    } else {
      snap();
    }
  }, [items, onChange]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleEnd);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleEnd]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Sync scroll when value changes from outside
  useEffect(() => {
    if (containerRef.current && !isDragging) {
      isSyncingRef.current = true;
      containerRef.current.scrollTo({
        top: targetScrollTop,
        behavior: "instant",
      });
      centerIndexRef.current = currentIndex;
      // Re-enable scroll listener after sync, update visual via rAF
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  }, [targetScrollTop, isDragging, currentIndex]);

  return (
    <div className="flex flex-col items-center h-28 relative">
      {/* Fade masks */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-surface to-transparent pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-surface to-transparent pointer-events-none z-10" />

      {/* Scroll container */}
      <div
        ref={containerRef}
        className="flex-1 w-full overflow-y-auto scrollbar-hide cursor-grab active:cursor-grabbing select-none"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleEnd}
        style={{ paddingTop: `${(CONTAINER_HEIGHT - ITEM_HEIGHT) / 2}px`, paddingBottom: `${(CONTAINER_HEIGHT - ITEM_HEIGHT) / 2}px` }}
      >
        <div className="flex flex-col">
          {items.map((item, idx) => {
            const activeIndex = isDragging ? centerIndex : currentIndex;
            const isCenter = idx === activeIndex;
            const distance = Math.abs(idx - activeIndex);
            const opacity = distance === 0 ? 1 : distance === 1 ? 0.5 : 0.2;
            return (
              <div
                key={item}
                className="flex items-center justify-start px-3 text-xs transition-all"
                style={{ height: `${ITEM_HEIGHT}px` }}
              >
                <span
                  className={`px-2 py-0.5 rounded transition-all ${
                    isCenter
                      ? "text-white font-semibold scale-110"
                      : "scale-90"
                  }`}
                  style={isCenter ? undefined : { opacity }}
                >
                  {item.toString().padStart(2, "0")}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selection indicator - positioned exactly at center */}
      <div
        className="absolute left-1 right-1 border border-accent/20 rounded-md pointer-events-none z-0"
        style={{
          top: "50%",
          height: `${ITEM_HEIGHT}px`,
          marginTop: `-${ITEM_HEIGHT / 2}px`,
          background: "rgba(var(--accent-rgb, 34 197 94), 0.1)"
        }}
      />
    </div>
  );
}

/* eslint-disable react-refresh/only-export-components */
// Parse time input into HH:MM format
// Exportée pour les tests unitaires
export function parseTimeInput(input: string): string | null {
  const trimmed = input.trim();

  // Empty input
  if (!trimmed) return null;

  // Format HH:MM
  const hhMmMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
  if (hhMmMatch) {
    const h = parseInt(hhMmMatch[1], 10);
    const m = parseInt(hhMmMatch[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }

  // Format HHMM (4 chiffres)
  const hhmmMatch = trimmed.match(/^(\d{4})$/);
  if (hhmmMatch) {
    const h = parseInt(hhmmMatch[1].substring(0, 2), 10);
    const m = parseInt(hhmmMatch[1].substring(2, 4), 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }

  // Format HMM (3 chiffres)
  const hmmMatch = trimmed.match(/^(\d{3})$/);
  if (hmmMatch) {
    const h = parseInt(hmmMatch[1].substring(0, 1), 10);
    const m = parseInt(hmmMatch[1].substring(1, 3), 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    }
  }

  // Format MM (1-2 chiffres = minutes seulement)
  const mmMatch = trimmed.match(/^(\d{1,2})$/);
  if (mmMatch) {
    const m = parseInt(mmMatch[1], 10);
    if (m >= 0 && m <= 59) {
      return `00:${m.toString().padStart(2, "0")}`;
    }
  }

  return null;
}

export function TimePicker({ value, onChange, className = "" }: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [allMinutes, setAllMinutes] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [hour, minute] = value.split(":").map(Number);
  const selectedHour = HOURS.includes(hour) ? hour : 9;

  const minutesList = allMinutes ? MINUTES_ALL : MINUTES_FIVE;
  const selectedMinute = minutesList.includes(minute) ? minute : (allMinutes ? 0 : 0);

  // Sync input ONLY when not editing and value changes from outside
  // Note: Ce pattern est nécessaire pour éviter que les changements externes n'écrasent l'édition
  useEffect(() => {
    if (!isEditing) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInputValue(value);
    }
  }, [value, isEditing]);

  const handleHourChange = (h: number) => {
    const newValue = `${h.toString().padStart(2, "0")}:${selectedMinute.toString().padStart(2, "0")}`;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleMinuteChange = (m: number) => {
    const newValue = `${selectedHour.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setIsEditing(true);
    setInputValue(val);

    const parsed = parseTimeInput(val);
    if (parsed) {
      onChange(parsed);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const step = e.shiftKey ? 5 : 1;
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const h = (selectedHour + 1) % 24;
      handleHourChange(h);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const h = (selectedHour - 1 + 24) % 24;
      handleHourChange(h);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      const m = (selectedMinute + step) % 60;
      handleMinuteChange(m);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const m = (selectedMinute - step + 60) % 60;
      handleMinuteChange(m);
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    // Reset to valid value on blur
    setInputValue(value);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleOutsideClick = useCallback((e: Event) => {
    if (
      panelRef.current &&
      !panelRef.current.contains(e.target as Node) &&
      !inputRef.current?.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [isOpen, handleOutsideClick]);

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onClick={handleClick}
        placeholder="HH:MM"
        maxLength={5}
        className="w-full px-3 py-2 text-xs text-ink rounded-md
          bg-surface border border-border/60 hover:border-accent/50
          focus:outline-none focus:border-accent/60 transition-colors text-left font-medium"
      />

      {isOpen && (
        <div
          ref={panelRef}
          className="absolute z-[9999] mt-1 left-1/2 -translate-x-1/2 w-36 max-w-[calc(100vw-1rem)] bg-surface border border-border/60 rounded-lg shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95"
        >
          <div className="flex divide-x divide-border/60 p-2">
            {/* Heures */}
            <div className="flex-1">
              <ScrollPicker
                items={HOURS}
                value={selectedHour}
                onChange={handleHourChange}
              />
            </div>

            {/* Minutes */}
            <div className="flex-1">
              <ScrollPicker
                items={minutesList}
                value={selectedMinute}
                onChange={handleMinuteChange}
              />
            </div>
          </div>

          {/* Toggle précision minutes */}
          <div className="flex items-center justify-center gap-2 py-2 px-3 border-t border-border/40">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allMinutes}
                onChange={(e) => setAllMinutes(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center
                ${allMinutes ? "bg-accent border-accent" : "border-border hover:border-accent/50"}`}>
                {allMinutes && (
                  <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <span className="text-[10px] text-ink-muted">Minutes précises</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
